import classNames from "classnames";
import { useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { useIsLoading, useRequestError } from "shared/hooks/requestStatusHooks";
import { selectFeatureFlag } from "shared/reducers/sysReducer";

import { getCase } from "app/actions/caseActions";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  useSelectedLab,
  useSelectedLabStation,
} from "hooks/useSelectedLabStation";
import AppError from "ui/components/AppError";
import CassetteBarcode from "ui/components/barcodes/block";
import BottleBarcode from "ui/components/barcodes/bottle";
import CassetteBarcodeIPC from "ui/components/barcodes/cassette-ipc";
import DocumentBarcode from "ui/components/barcodes/document";
import SlideBarcode from "ui/components/barcodes/slide";
import Spinner from "ui/components/Spinner";

const PrintPage = () => {
  const dispatch = useAppDispatch();
  const { case_id } = useParams();
  const caseId = Number(case_id);
  const singlePage = useAppSelector((state) =>
    selectFeatureFlag(state, "singlePageBarcodePrinting")
  );

  const [searchParams] = useSearchParams();

  const selectedLabStation = useSelectedLabStation();
  const selectedLab = useSelectedLab();
  const printCase = useAppSelector((state) =>
    state.case?.id === caseId ? state.case : undefined
  );
  const printCaseLoading = useIsLoading(`case/${caseId}`);
  const printCaseError = useRequestError(`case/${caseId}`);

  const printer = searchParams.get("printer");

  const objects = Object.fromEntries(
    ["documents", "specimens", "blocks", "slides"].map((object) => {
      const ids = searchParams
        .get(object)
        ?.split(",")
        .map((id) => Number(id));
      return [object, ids?.length ? ids : undefined];
    })
  );

  useEffect(() => {
    if (caseId && !printCase) {
      dispatch(
        getCase({
          id: caseId,
          parentLookupLabStationId: selectedLabStation.id,
        })
      ).catch((e) => e); // error available in state. not the best pattern to need to catch here
    }
  }, [caseId, printCase]);

  useEffect(() => {
    if (window.opener) {
      const closeWindow = () => {
        window.close();
      };
      window.addEventListener("afterprint", closeWindow);

      return () => {
        window.removeEventListener("afterprint", closeWindow);
      };
    }
  }, []);

  useEffect(() => {
    if (window.opener && printCase) {
      const print = () => {
        setTimeout(() => window.print(), 250);
      };
      if (document.readyState === "complete") {
        print();
      } else {
        window.addEventListener("load", print);
        return () => window.removeEventListener("load", print);
      }
    }
  }, [printCase]);

  if (printCaseLoading) return <Spinner />;

  if (printCaseError)
    return printCaseError.status === 404 ? (
      <div className="PageError">
        <h1>Label Not Found</h1>
        <p>
          If this label is from a case accessioned outside of{" "}
          <strong>{selectedLab.name}</strong>, return to your{" "}
          <a href="/">{selectedLabStation.name} station</a> and try changing the
          lab.
        </p>
        <p>
          For additional support, please contact{" "}
          <a href="mailto@support@pathologywatch.com">
            support@pathologywatch.com
          </a>
        </p>
      </div>
    ) : (
      <AppError />
    );

  if (!printCase) return null;

  return (
    <div className={classNames("Print", { SinglePagePrint: singlePage })}>
      {objects.slides?.map((slide_id) => (
        <SlideBarcode
          key={`slide-${slide_id}`}
          id={slide_id}
          printCase={printCase}
          printer={printer}
        />
      ))}
      {objects.blocks?.map((block_id) =>
        printer === "ipc" ? (
          <CassetteBarcodeIPC
            key={`cassette-${block_id}`}
            id={block_id}
            printCase={printCase}
          />
        ) : (
          <CassetteBarcode
            key={`cassette-${block_id}`}
            id={block_id}
            printCase={printCase}
            printer={printer}
          />
        )
      )}
      {objects.specimens?.map((specimen_id) => (
        <BottleBarcode
          key={`bottle-${specimen_id}`}
          id={specimen_id}
          printCase={printCase}
        />
      ))}
      {objects.documents?.map((document_id) => (
        <DocumentBarcode
          key={`document-${document_id}`}
          id={document_id}
          printCase={printCase}
        />
      ))}
    </div>
  );
};

export default PrintPage;
